/**
 * Centralized query key management for consistent cache invalidation
 *
 * Hierarchy:
 * ['feedbacks']
 *   ['feedbacks', 'stats', tenantDomain]
 *   ['feedbacks', 'recent', tenantDomain]
 *   ['feedbacks', 'list', tenantDomain, filters]
 * ['meetings']
 *   ['meetings', 'list', tenantDomain, status]
 * ['okrs']
 *   ['okrs', 'progress', tenantDomain]
 *   ['okrs', 'list', tenantDomain, filters]
 *   ['okrs', 'score', tenantDomain, okrId]
 *   ['okrs', 'scores', tenantDomain]
 * ['evaluations']
 *   ['evaluations', 'my', tenantDomain, filters]
 *   ['evaluations', 'employee', tenantDomain, employeeId, filters]
 *   ['evaluations', 'templates', tenantDomain]
 * ['climate']
 *   ['climate', 'trends', tenantDomain, params]
 *   ['climate', 'insights', tenantDomain]
 * ['evaluator-assignments']
 *   ['evaluator-assignments', 'queue', tenantDomain, pagination]
 *   ['evaluator-assignments', 'my-queue', tenantDomain]
 */
export const feedbackKeys = {
  all: ['feedbacks'] as const,
  stats: (tenantDomain: string) =>
    [...feedbackKeys.all, 'stats', tenantDomain] as const,
  recent: (tenantDomain: string) =>
    [...feedbackKeys.all, 'recent', tenantDomain] as const,
  lists: () => [...feedbackKeys.all, 'list'] as const,
  list: (tenantDomain: string, filters?: Record<string, any>) =>
    [...feedbackKeys.lists(), tenantDomain, filters] as const,
}

export const meetingKeys = {
  all: ['meetings'] as const,
  lists: () => [...meetingKeys.all, 'list'] as const,
  list: (tenantDomain: string, status?: string) =>
    [...meetingKeys.lists(), tenantDomain, status] as const,
}

export const okrKeys = {
  all: ['okrs'] as const,
  progress: (tenantDomain: string) =>
    [...okrKeys.all, 'progress', tenantDomain] as const,
  lists: () => [...okrKeys.all, 'list'] as const,
  list: (tenantDomain: string, filters?: Record<string, any>) =>
    [...okrKeys.lists(), tenantDomain, filters] as const,
  score: (tenantDomain: string, okrId: string) =>
    [...okrKeys.all, 'score', tenantDomain, okrId] as const,
  scores: (tenantDomain: string) =>
    [...okrKeys.all, 'scores', tenantDomain] as const,
  individualAll: (tenantDomain: string, params?: Record<string, any>) =>
    [...okrKeys.all, 'individual-all', tenantDomain, params] as const,
}

export const evaluationKeys = {
  all: ['evaluations'] as const,
  my: (tenantDomain: string, filters?: Record<string, any>) =>
    [...evaluationKeys.all, 'my', tenantDomain, filters] as const,
  createdByMe: (tenantDomain: string, filters?: Record<string, any>) =>
    [...evaluationKeys.all, 'createdByMe', tenantDomain, filters] as const,
  employee: (tenantDomain: string, employeeId: string, filters?: Record<string, any>) =>
    [...evaluationKeys.all, 'employee', tenantDomain, employeeId, filters] as const,
  templates: (tenantDomain: string) =>
    [...evaluationKeys.all, 'templates', tenantDomain] as const,
  myTemplate: (tenantDomain: string) =>
    [...evaluationKeys.all, 'myTemplate', tenantDomain] as const,
}

export const climateKeys = {
  all: ['climate'] as const,
  trends: (tenantDomain: string, params?: Record<string, any>) =>
    [...climateKeys.all, 'trends', tenantDomain, params] as const,
  insights: (tenantDomain: string) =>
    [...climateKeys.all, 'insights', tenantDomain] as const,
}

export const evaluatorKeys = {
  all: ['evaluator-assignments'] as const,
  queue: (tenantDomain: string, pagination?: { limit?: number; offset?: number }) =>
    [...evaluatorKeys.all, 'queue', tenantDomain, pagination] as const,
  myQueue: (tenantDomain: string) =>
    [...evaluatorKeys.all, 'my-queue', tenantDomain] as const,
}

export const analyticsKeys = {
  all: ['analytics'] as const,
  performanceByTheme: (tenantDomain: string) =>
    [...analyticsKeys.all, 'performance-by-theme', tenantDomain] as const,
}
